import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { ChevronLeft, ChevronRight, ShieldCheck } from 'lucide-react';

function UserPricePage() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [submittingPackageId, setSubmittingPackageId] = useState(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);

    window.addEventListener('resize', handleResize);
    fetchData();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/user/active-pricelist`, {
        withCredentials: true,
      });
      setData(res.data.data || []);
    } catch (err) {
      Swal.fire('Oops!', err.response?.data?.message || 'Silakan coba lagi.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePrev = () => setCurrentIndex((prev) => (prev - 1 + data.length) % data.length);
  const handleNext = () => setCurrentIndex((prev) => (prev + 1) % data.length);

  const getVisibleCards = () => {
    if (data.length === 0) return [];

    if (isMobile || data.length === 1) {
      return [{ ...data[currentIndex % data.length], _highlight: true }];
    }

    if (data.length === 2) {
      const left = currentIndex % 2;
      const right = (currentIndex + 1) % 2;
      return [
        { ...data[left], _highlight: false },
        { ...data[right], _highlight: true },
      ];
    }

    const left = (currentIndex - 1 + data.length) % data.length;
    const center = currentIndex % data.length;
    const right = (currentIndex + 1) % data.length;

    return [
      { ...data[left], _highlight: false },
      { ...data[center], _highlight: true },
      { ...data[right], _highlight: false },
    ];
  };

  const visibleCards = getVisibleCards();

  const openModal = (pkg) => {
    setSelectedPackage(pkg);
    setShowModal(true);
  };

  const closeModal = () => {
    setSelectedPackage(null);
    setShowModal(false);
  };

  const confirmSelection = async () => {
    if (!selectedPackage || submittingPackageId) return;

    setSubmittingPackageId(selectedPackage.id);

    Swal.fire({
      title: 'Memuat...',
      text: 'Mohon tunggu, sedang menyiapkan pembayaran Midtrans.',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/payment/create-transaction`,
        { id_price: selectedPackage.id },
        { withCredentials: true },
      );

      Swal.close();
      setShowModal(false);
      window.location.href = res.data.redirect_url;
    } catch (err) {
      Swal.close();
      Swal.fire('Error', err.response?.data?.message || 'Gagal memulai pembayaran', 'error');
    } finally {
      setSubmittingPackageId(null);
    }
  };

  return (
    <div className="tw w-full px-2">
      <h3 className="mb-6 text-center text-2xl font-semibold text-slate-800">Pilih Paket</h3>

      {loading ? (
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : data.length > 0 ? (
        <div className="flex flex-col items-center">
          <div className="flex w-full max-w-6xl items-center justify-center gap-2">
            <button
              type="button"
              className="rounded-xl border border-slate-300 bg-white p-2 text-slate-700 hover:bg-slate-50"
              onClick={handlePrev}
            >
              <ChevronLeft size={18} />
            </button>

            <div className="flex w-full justify-center gap-3 overflow-hidden px-1">
              {visibleCards.map((item) => {
                const highlight = item._highlight;

                return (
                  <div
                    key={item.id}
                    className={`w-full max-w-sm rounded-2xl border p-4 shadow-sm transition ${
                      highlight
                        ? 'border-slate-900 bg-white scale-[1.02]'
                        : 'border-slate-200 bg-slate-50 opacity-70'
                    }`}
                  >
                    <div className="text-center">
                      <h5 className="text-lg font-semibold text-slate-800">{item.name}</h5>
                      <p className="text-sm text-slate-500">{item.code}</p>
                      <h3 className="mt-3 text-2xl font-bold text-slate-900">Rp {parseInt(item.price, 10).toLocaleString('id-ID')}</h3>
                      <p className="mt-1 text-sm text-slate-600">
                        {item.duration_type === 'Permanent'
                          ? 'Permanent'
                          : `${item.duration_value} ${item.duration_type}(s)`}
                      </p>
                    </div>

                    <hr className="my-4 border-slate-200" />

                    <div className="space-y-1 text-sm text-slate-700">
                      <FeatureRow label="Max. Session" value={item.session_create_max === -1 ? 'Unlimited' : item.session_create_max} />
                      <FeatureRow label="Max. Chat Attempts" value={item.chat_attempt_max === -1 ? 'Unlimited' : item.chat_attempt_max} />
                      <FeatureRow label="Max. AI Token" value={item.max_ai_token} />
                      <FeatureRow
                        label="Voice to Voice AI"
                        value={item.is_ai_audio_active === 1 ? 'YES' : 'NO'}
                        valueClassName={item.is_ai_audio_active === 1 ? 'text-emerald-600' : 'text-rose-600'}
                      />
                      <FeatureRow label="Max. Voice Attempts" value={item.voice_attempt_max === -1 ? 'Unlimited' : item.voice_attempt_max} />
                      <FeatureRow
                        label="Max. Examination Assistant"
                        value={item.examination_assist_max === -1 ? 'Unlimited' : item.examination_assist_max}
                      />
                    </div>

                    <button
                      type="button"
                      className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
                      onClick={() => openModal(item)}
                    >
                      <ShieldCheck size={15} />
                      Pilih Paket
                    </button>
                  </div>
                );
              })}
            </div>

            <button
              type="button"
              className="rounded-xl border border-slate-300 bg-white p-2 text-slate-700 hover:bg-slate-50"
              onClick={handleNext}
            >
              <ChevronRight size={18} />
            </button>
          </div>

          <div className="mt-5 text-xs text-slate-500">
            {data.length > 0 ? `${(currentIndex % data.length) + 1} / ${data.length}` : '0 / 0'}
          </div>
        </div>
      ) : (
        <div className="text-center text-red-600">Tidak ada paket yang tersedia</div>
      )}

      {showModal && selectedPackage ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <h5 className="text-base font-semibold text-slate-800">Konfirmasi Paket</h5>
              <button
                type="button"
                className="rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50"
                onClick={closeModal}
              >
                X
              </button>
            </div>

            <div className="px-5 py-4 text-sm text-slate-600">
              Apakah Anda yakin ingin memilih paket <strong>{selectedPackage.name}</strong> dengan harga{' '}
              <strong>Rp {parseInt(selectedPackage.price, 10).toLocaleString('id-ID')}</strong>?
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-4">
              <button
                type="button"
                className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                onClick={closeModal}
              >
                Batal
              </button>
              <button
                type="button"
                className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
                onClick={confirmSelection}
                disabled={submittingPackageId === selectedPackage.id}
              >
                {submittingPackageId === selectedPackage.id ? 'Menyiapkan Pembayaran...' : 'Ya, Pilih Paket'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function FeatureRow({ label, value, valueClassName = 'text-slate-900' }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span>{label}</span>
      <span className={`font-semibold ${valueClassName}`}>{value}</span>
    </div>
  );
}

export default UserPricePage;
